// frontend/src/widgets/QuoteStatusSummaryWidget.jsx
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2026
// Contact:      dev@castle-traffic.co.uk
// Created:      2026-01-28
// Updated:      2026-09-09 14:11 BST
//
// Description:
//   Quotes by Status summary widget.
//   - Loads visible quotes via GET /api/quotes
//   - Computes Qty + Total by status (client-side; backend unchanged)
//
// Output columns:
//   Status | Qty | Total
//
// IMPORTANT:
//   Uses shared Axios instance ONLY.
//
// Mobile Responsiveness (2026-09-09):
//   - Preserves both quote GETs, selected-currency handling and client-side aggregation.
//   - Desktop retains the existing Status | Qty | Total summary layout.
//   - Mobile hides the desktop column header and presents each status as a labelled card.
//   - Mobile records viewport shows one complete status card per vertical scroll/snap.
//   - Dynamic mobile growth uses the proven Vision pattern:
//       restore previous host sizing;
//       clear temporary viewport/root sizing;
//       measure the natural first status card;
//       set the records viewport to exactly one complete card;
//       wait a second animation frame;
//       resize the first width-matching fixed-height mobile host.
//   - ResizeObserver watches the first status card; state changes retrigger sizing.
//   - Existing portal tooltip mouse handlers are preserved unchanged.
//   - No API routes, aggregation logic, currency logic or status ordering are changed.
//
// Operational Notes:
//   - One render tree is shared by desktop and mobile.
//   - Desktop keeps the existing complete eight-row summary.

import React, { useEffect, useLayoutEffect, useMemo, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { useTheme } from "../context/ThemeContext.jsx";
import api from "../api/axiosInstance";

const norm = (v) => String(v || "").trim().toLowerCase();

const titleCase = (s) => {
  const labels = {
    draft: "Draft",
    submitted: "Submitted",
    approved: "Approved",
    rejected: "Rejected",
    sent: "Sent",
    accepted: "Accepted",
    expired: "Expired",
    withdrawn: "Withdrawn",
  };

  const k = norm(s);
  if (labels[k]) return labels[k];

  return String(s || "")
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const QUOTE_STATUSES = ["draft", "submitted", "approved", "rejected", "sent", "accepted", "expired", "withdrawn"];

function formatMoney(amount, currency) {
  const n = Number(amount);
  if (!Number.isFinite(n)) return "—";
  const cur = String(currency || "GBP").toUpperCase();
  try {
    return new Intl.NumberFormat("en-GB", { style: "currency", currency: cur }).format(n);
  } catch {
    return `${cur} ${n.toFixed(2)}`;
  }
}

function statusDot(theme, status) {
  const s = norm(status);

  if (s === "approved" || s === "accepted") return "#16a34a";
  if (s === "rejected" || s === "withdrawn") return "#dc2626";
  if (s === "sent") return "#2563eb";
  if (s === "submitted") return "#0ea5e9";
  if (s === "expired") return "#6b7280";

  return theme?.accent_color || "#ff7a00";
}

function extractQuoteList(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.results)) return payload.results;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.quotes)) return payload.quotes;
  return [];
}

// ---------- Gold-standard tooltip (portal, multiline, no native title) ----------
function usePortalTooltip(theme) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const rafRef = useRef(null);

  const hide = useCallback(() => {
    setOpen(false);
    setText("");
  }, []);

  const onMove = useCallback((e) => {
    const x = e?.clientX || 0;
    const y = e?.clientY || 0;
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => setPos({ x, y }));
  }, []);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && hide();
    const onScroll = () => hide();
    const onBlur = () => hide();

    window.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("blur", onBlur);

    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("blur", onBlur);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [hide]);

  const tooltipNode = useMemo(() => {
    if (!open || !text) return null;

    const pad = 12;
    const vw = typeof window !== "undefined" ? window.innerWidth : 1200;
    const vh = typeof window !== "undefined" ? window.innerHeight : 800;

    const left = Math.min(vw - pad, Math.max(pad, pos.x + 14));
    const top = Math.min(vh - pad, Math.max(pad, pos.y + 14));

    return createPortal(
      <div
        style={{
          position: "fixed",
          left,
          top,
          zIndex: 999999,
          pointerEvents: "none",
          background: theme?.primary_color || "#111827",
          color: "#fff",
          padding: "10px 12px",
          borderRadius: 12,
          boxShadow: "0 10px 25px rgba(0,0,0,0.25)",
          fontSize: 12,
          fontWeight: 800,
          lineHeight: "16px",
          maxWidth: 360,
          whiteSpace: "pre-line",
        }}
      >
        {text}
      </div>,
      document.body
    );
  }, [open, pos.x, pos.y, text, theme]);

  const bind = useMemo(
    () => ({
      onMouseEnter: (t) => {
        setText(String(t || ""));
        setOpen(true);
      },
      onMouseLeave: () => hide(),
      onMouseMove: onMove,
    }),
    [hide, onMove]
  );

  return { tooltipNode, bind };
}

export default function QuoteStatusSummaryWidget({ refreshKey, selectedQuoteId }) {
  const { theme } = useTheme();
  const tooltip = usePortalTooltip(theme);

  const widgetRootRef = useRef(null);
  const recordsViewportRef = useRef(null);
  const firstRowRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const [summary, setSummary] = useState(() => {
    const base = {};
    QUOTE_STATUSES.forEach((s) => (base[s] = { qty: 0, total: 0, currency: "GBP" }));
    return base;
  });

  const [selectedCurrency, setSelectedCurrency] = useState("GBP");

  useEffect(() => {
    if (!selectedQuoteId) {
      setSelectedCurrency("GBP");
      return;
    }
    let alive = true;
    api
      .get(`/api/quotes/${selectedQuoteId}`)
      .then((res) => {
        if (!alive) return;
        const cur = String(res?.data?.currency || "GBP").toUpperCase();
        setSelectedCurrency(cur || "GBP");
      })
      .catch(() => alive && setSelectedCurrency("GBP"));
    return () => {
      alive = false;
    };
  }, [selectedQuoteId, refreshKey]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setErr("");

    api
      .get("/api/quotes", { params: { limit: 2000, offset: 0 } })
      .then((res) => {
        if (!alive) return;

        const arr = extractQuoteList(res?.data);
        const base = {};
        QUOTE_STATUSES.forEach((s) => (base[s] = { qty: 0, total: 0, currency: "GBP" }));

        for (const q of arr) {
          const st = norm(q?.status);
          if (!base[st]) continue;

          base[st].qty += 1;

          const total = Number(q?.total);
          if (Number.isFinite(total)) base[st].total += total;

          const cur = String(q?.currency || "").trim();
          if (cur) base[st].currency = cur.toUpperCase();
        }

        setSummary(base);
      })
      .catch((e) => {
        if (!alive) return;
        setErr(e?.response?.data?.detail || e?.message || "Failed to load quote summary");
        const base = {};
        QUOTE_STATUSES.forEach((s) => (base[s] = { qty: 0, total: 0, currency: "GBP" }));
        setSummary(base);
      })
      .finally(() => alive && setLoading(false));

    return () => {
      alive = false;
    };
  }, [refreshKey]);

  const card = useMemo(
    () => ({
      background: theme?.card_bg || "#fff",
      borderRadius: 14,
      padding: 14,
      boxShadow: theme?.card_shadow || "0 1px 2px rgba(0,0,0,0.06)",
      border: theme?.card_border || "1px solid rgba(0,0,0,0.06)",
      minHeight: 0,
      boxSizing: "border-box",
      overflowWrap: "anywhere",
    }),
    [theme]
  );

  useLayoutEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) {
      return undefined;
    }

    const root = widgetRootRef.current;
    const viewport = recordsViewportRef.current;
    const firstRow = firstRowRef.current;

    if (!root || !viewport) {
      return undefined;
    }

    const mediaQuery = window.matchMedia("(max-width: 767px)");

    let animationFrame = 0;
    let hostRecord = null;

    const restoreHost = () => {
      if (!hostRecord?.node) return;

      hostRecord.node.style.height = hostRecord.height;
      hostRecord.node.style.minHeight = hostRecord.minHeight;
      hostRecord = null;
    };

    const findFixedMobileHost = () => {
      const rootRect = root.getBoundingClientRect();
      const candidates = [];

      let node = root.parentElement;
      let depth = 0;

      while (node && node !== document.body && depth < 6) {
        const rect = node.getBoundingClientRect();
        const inlineHeight = String(node.style?.height || "").trim();

        const widthMatches =
          Math.abs(rect.width - rootRect.width) < 90;

        const hasFixedInlineHeight =
          inlineHeight &&
          inlineHeight !== "auto" &&
          inlineHeight !== "100%" &&
          inlineHeight !== "100vh";

        if (widthMatches && hasFixedInlineHeight) {
          candidates.push(node);
        }

        node = node.parentElement;
        depth += 1;
      }

      return candidates.length ? candidates[0] : null;
    };

    const applyMobileSizing = () => {
      cancelAnimationFrame(animationFrame);
      restoreHost();

      viewport.style.height = "";
      viewport.style.minHeight = "";
      root.style.height = "";
      root.style.minHeight = "";

      if (!mediaQuery.matches) {
        return;
      }

      animationFrame = requestAnimationFrame(() => {
        const currentFirstRow = firstRowRef.current;

        if (currentFirstRow) {
          const rowHeight = Math.ceil(
            Math.max(
              currentFirstRow.getBoundingClientRect().height || 0,
              currentFirstRow.scrollHeight || 0
            )
          );

          if (Number.isFinite(rowHeight) && rowHeight > 0) {
            viewport.style.height = `${rowHeight}px`;
            viewport.style.minHeight = `${rowHeight}px`;
          }
        }

        root.style.height = "auto";
        root.style.minHeight = "0px";

        animationFrame = requestAnimationFrame(() => {
          const host = findFixedMobileHost();

          if (!host) {
            return;
          }

          hostRecord = {
            node: host,
            height: host.style.height,
            minHeight: host.style.minHeight,
          };

          const hostRect = host.getBoundingClientRect();
          const rootRect = root.getBoundingClientRect();

          const wrapperChromeHeight = Math.max(
            0,
            rootRect.top - hostRect.top
          );

          const requiredHostHeight = Math.ceil(
            Math.max(
              host.scrollHeight,
              wrapperChromeHeight + rootRect.height + 8
            )
          );

          if (
            !Number.isFinite(requiredHostHeight) ||
            requiredHostHeight <= 0
          ) {
            return;
          }

          host.style.height = `${requiredHostHeight}px`;
          host.style.minHeight = `${requiredHostHeight}px`;
        });
      });
    };

    applyMobileSizing();

    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(() => applyMobileSizing())
        : null;

    if (resizeObserver && firstRow) {
      resizeObserver.observe(firstRow);
    }

    const handleMediaChange = () => applyMobileSizing();

    mediaQuery.addEventListener?.("change", handleMediaChange);
    window.addEventListener("resize", applyMobileSizing);

    return () => {
      cancelAnimationFrame(animationFrame);
      resizeObserver?.disconnect();

      mediaQuery.removeEventListener?.("change", handleMediaChange);
      window.removeEventListener("resize", applyMobileSizing);

      restoreHost();

      viewport.style.height = "";
      viewport.style.minHeight = "";
      root.style.height = "";
      root.style.minHeight = "";
    };
  }, [
    err,
    loading,
    refreshKey,
    selectedCurrency,
    summary,
  ]);

  return (
    <div ref={widgetRootRef} className="h-auto min-h-0 w-full overflow-visible" style={card}>
      <style>{`
        .qs-head {
          display: grid;
          grid-template-columns: 1fr 64px 140px;
          gap: 10px;
          font-size: 12px;
          font-weight: 950;
          opacity: 0.85;
          padding-bottom: 8px;
          border-bottom: 1px solid rgba(0,0,0,0.08);
          margin-bottom: 10px;
        }

        .qs-row {
          display: grid;
          grid-template-columns: 1fr 64px 140px;
          gap: 10px;
          align-items: center;
          padding: 10px 12px;
          border-radius: 12px;
          border: 1px solid rgba(0,0,0,0.06);
          background: #fff;
          margin-bottom: 10px;
          font-size: 12px;
          font-weight: 950;
        }
        .qs-row:last-child { margin-bottom: 0; }

        .qs-status {
          display: flex;
          gap: 10px;
          align-items: center;
          min-width: 0;
        }
        .qs-dot { width: 10px; height: 10px; border-radius: 999px; background: rgba(0,0,0,0.2); }
        .qs-name { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

        .qs-qty, .qs-total { text-align: right; font-variant-numeric: tabular-nums; }

        @media (max-width: 767px) {
          .qs-head {
            display: none;
          }

          .qs-records {
            overflow-y: auto;
            overflow-x: hidden;
            overscroll-behavior-y: contain;
            -webkit-overflow-scrolling: touch;
            scrollbar-width: none;
            scroll-snap-type: y mandatory;
          }

          .qs-records::-webkit-scrollbar {
            display: none;
          }

          .qs-row {
            grid-template-columns: minmax(0, 1fr) minmax(72px, auto);
            grid-template-areas:
              "status status"
              "qty total";
            gap: 8px 12px;
            padding: 10px 12px;
            margin-bottom: 0;
            border-radius: 12px;
            scroll-snap-align: start;
            scroll-snap-stop: always;
          }

          .qs-status {
            grid-area: status;
            gap: 8px;
            padding-bottom: 7px;
            border-bottom: 1px solid rgba(0,0,0,0.06);
          }

          .qs-dot {
            flex: 0 0 9px;
            width: 9px;
            height: 9px;
          }

          .qs-name {
            white-space: normal;
            overflow: visible;
            text-overflow: clip;
            font-size: 11px;
            line-height: 14px;
          }

          .qs-qty,
          .qs-total {
            position: relative;
            min-width: 0;
            padding-top: 15px;
            font-size: 11px;
            line-height: 14px;
            overflow-wrap: anywhere;
          }

          .qs-qty {
            grid-area: qty;
            text-align: left;
          }

          .qs-total {
            grid-area: total;
            text-align: right;
          }

          .qs-qty::before,
          .qs-total::before {
            position: absolute;
            top: 0;
            font-size: 9px;
            line-height: 11px;
            font-weight: 900;
            opacity: 0.58;
            text-transform: uppercase;
            letter-spacing: 0.25px;
          }

          .qs-qty::before {
            left: 0;
            content: "Qty";
          }

          .qs-total::before {
            right: 0;
            content: "Total";
          }
        }
      `}</style>

      {tooltip.tooltipNode}

      <div className="flex flex-col gap-1 sm:flex-row sm:items-baseline sm:justify-between sm:gap-2.5">
        <div style={{ fontWeight: 950 }}>Quotes by Status</div>
        <div style={{ fontSize: 12, opacity: 0.75, fontWeight: 850 }}>
          Currency: {String(selectedCurrency || "GBP").toUpperCase()}
        </div>
      </div>

      <div className="qs-head" style={{ marginTop: 10 }}>
        <div>Status</div>
        <div style={{ textAlign: "right" }}>Qty</div>
        <div style={{ textAlign: "right" }}>Total</div>
      </div>

      {err ? <div className="break-words" style={{ color: "#b91c1c", fontWeight: 950, fontSize: 12, marginBottom: 10, overflowWrap: "anywhere" }}>{err}</div> : null}
      {loading ? <div style={{ fontSize: 12, opacity: 0.75, fontWeight: 900 }}>Loading…</div> : null}

      {!loading ? (
        <div
          ref={recordsViewportRef}
          className="qs-records"
          style={{ display: "grid", gap: 0 }}
        >
          {QUOTE_STATUSES.map((s, index) => {
            const row = summary?.[s] || { qty: 0, total: 0, currency: "GBP" };
            const cur = String(selectedCurrency || row.currency || "GBP").toUpperCase();
            const dot = statusDot(theme, s);

            return (
              <div
                key={s}
                ref={index === 0 ? firstRowRef : null}
                className="qs-row"
                onMouseEnter={() =>
                  tooltip.bind.onMouseEnter(
                    `${titleCase(s)}\nQty: ${row.qty}\nTotal: ${formatMoney(row.total, cur)}`
                  )
                }
                onMouseLeave={tooltip.bind.onMouseLeave}
                onMouseMove={tooltip.bind.onMouseMove}
              >
                <div className="qs-status">
                  <span className="qs-dot" style={{ background: dot }} />
                  <span className="qs-name">{titleCase(s)}</span>
                </div>
                <div className="qs-qty">{row.qty}</div>
                <div className="qs-total">{formatMoney(row.total, cur)}</div>
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
